import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Presentation, Save, RotateCcw } from "lucide-react";

/**
 * iter177 — Pitch Deck Manager
 * -----------------------------------------------------------------
 * Super-Admin control that swaps out the Canva iframe on
 * /business/pubs at runtime — no redeploy needed. Backend endpoint
 * `PUT /api/marketing/pitch-deck` persists into `marketing_content`
 * keyed by "pitch_deck"; `GET` is public so BusinessPubsPage can
 * fetch anonymously.
 */
export default function PitchDeckManagerCard() {
  const [url, setUrl] = useState("");
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null, is_default: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketing/pitch-deck");
      setUrl(data.is_default ? "" : data.canva_embed_url || "");
      setMeta({
        updated_at: data.updated_at || null,
        updated_by: data.updated_by || null,
        is_default: !!data.is_default,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // iter177 — pass the URL explicitly so `reset()` can force the empty
  // string without racing the `url` state update.
  const save = async (explicitUrl) => {
    setSaving(true);
    try {
      const value = (explicitUrl !== undefined ? explicitUrl : url).trim();
      const { data } = await api.put("/marketing/pitch-deck", {
        canva_embed_url: value,
      });
      setUrl(data.is_default ? "" : data.canva_embed_url || "");
      setMeta({
        updated_at: data.updated_at || null,
        updated_by: data.updated_by || null,
        is_default: !!data.is_default,
      });
      toast.success(
        data.is_default
          ? "Pitch deck reset to the default Canva embed"
          : "Pitch deck updated — /business/pubs will pick it up on next load"
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (!window.confirm("Clear the custom Canva embed and fall back to the platform default?")) return;
    // Pass an explicit empty string — do NOT rely on state having flushed
    // (React closes over the pre-render value of `url`). The backend
    // treats empty as "clear the override".
    save("");
  };

  return (
    <section
      className="mt-12 border border-zinc-200 rounded-sm bg-white p-6"
      data-testid="pitch-deck-manager-card"
    >
      <header className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
            <Presentation className="h-3.5 w-3.5" /> Pitch deck manager
          </p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mt-1">
            Swap the /business/pubs Canva deck.
          </h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Paste a Canva embed URL. It replaces the iframe on
            <code className="mx-1 bg-zinc-100 px-1 py-0.5 rounded text-xs">/business/pubs</code>
            for every visitor. Leave it blank (or hit reset) to fall back to the
            platform default.
          </p>
        </div>
      </header>

      {loading ? (
        <p className="text-sm uppercase tracking-[0.18em] text-zinc-500">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div>
            <Label
              htmlFor="pitch-canva-url"
              className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500"
            >
              Canva embed URL
            </Label>
            <Input
              id="pitch-canva-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.canva.com/design/…/view?embed"
              data-testid="pitch-deck-url-input"
              className="mt-1 h-10 rounded-sm border-zinc-300 font-mono text-sm"
            />
            <p className="text-[11px] text-zinc-500 mt-1.5">
              In Canva: <em>Share → More → Embed</em> and copy the URL. It should
              start with <code className="bg-zinc-100 px-1 rounded">https://www.canva.com/design/…</code>
              and end with <code className="bg-zinc-100 px-1 rounded">?embed</code>.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              type="button"
              onClick={() => save()}
              disabled={saving}
              data-testid="pitch-deck-save-btn"
              className="h-10 rounded-sm bg-zinc-950 hover:bg-zinc-800 text-white font-semibold"
            >
              <Save className="h-4 w-4 mr-1.5" />
              {saving ? "Saving…" : "Save pitch deck URL"}
            </Button>
            <Button
              type="button"
              onClick={reset}
              disabled={saving || meta.is_default}
              variant="outline"
              data-testid="pitch-deck-reset-btn"
              className="h-10 rounded-sm"
            >
              <RotateCcw className="h-4 w-4 mr-1.5" />
              Reset to default
            </Button>
          </div>

          <div className="text-[11px] text-zinc-500 pt-2 border-t border-zinc-100" data-testid="pitch-deck-status">
            {meta.is_default ? (
              <span>Currently using the <strong>platform default</strong> deck.</span>
            ) : (
              <span>
                Last saved by <strong>{meta.updated_by || "unknown"}</strong>
                {meta.updated_at ? ` on ${new Date(meta.updated_at).toLocaleString()}` : ""}.
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
